


















































































































import { Vue, Component, Ref } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import { TournamentEntity } from '@/api/tournament'
import { TournamentTeamEntity, TournamentTeamState } from '@/api/tournamentTeam'
import { getElementScrollRemaining } from '@/utils/window'
import tournamentStore from '@/modules/tournament/tournament.store'
import TournamentDetailsHeader from '@/modules/tournament/components/TournamentDetailsHeader'
import UserAvatar from '@/modules/shared/components/UserAvatar'
import { getRealtimeConnection, realtimeEventTarget } from '@/utils/realtime'
import { HubConnection } from '@microsoft/signalr'
import { CancelError, CancellationToken } from '@/utils/cancel'
import { TournamentTeamDialog } from '@/modules/tournament/components/TournamentTeamDialog'

@Component({
  components: {
    TournamentDetailsHeader,
    UserAvatar,
    TournamentTeamDialog
  }
})
export default class TournamentDetailsTeams extends Vue {
  tournamentModule = getModule(tournamentStore, this.$store)
  page: number = 1
  appendDataAllowed: boolean = true
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()
  isInitializing: boolean = true

  isTournamentTeamDialogVisible: boolean = false
  selectedTournamentTeamId: string = ''

  @Ref('teamList') readonly teamList!: HTMLElement

  get hasTeams() {
    return this.tournamentTeams && this.tournamentTeams.length > 0
  }

  /* Vuex State */
  get tournament(): TournamentEntity | null {
    return this.tournamentModule.selectedTournament
  }

  get hasMorePage() {
    return this.tournamentModule.selectedTournamentPlayerList.hasMorePage
  }

  get tournamentTeams(): TournamentTeamEntity[] {
    return this.tournamentModule.selectedTournamentPlayerList.items
  }

  /* Hooks */
  async created() {
    this.isInitializing = true
    await this.fetchTournamentTeams()
    await this.subscribeRealtimeEvents()
    realtimeEventTarget.addEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.addEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
  }

  async beforeDestroy() {
    this.tournamentModule.resetSelectedTournamentTeams()
    this.unsubscribeRealtimeEvents()
    realtimeEventTarget.removeEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.removeEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
  }

  /* Methods */
  async fetchTournamentTeams() {
    await this.tournamentModule.setSelectedTournamentTeams({
      tournamentId: this.tournament!.tournamentId,
      page: this.page
    })

    setTimeout(async () => {
      if (this.teamList) {
        const checkOverflow = (elem: HTMLElement) => {
          const elemWidth = elem.getBoundingClientRect().width
          const parentWidth = elem.parentElement!.getBoundingClientRect().width

          return elemWidth > parentWidth
        }

        if (!checkOverflow(this.teamList) && this.hasMorePage) {
          this.page = this.page + 1
          this.appendDataAllowed = false
          await this.fetchTournamentTeams()
        }
      }
    })

    this.appendDataAllowed = this.hasMorePage
    this.isInitializing = false
  }

  getCaptainName(team: TournamentTeamEntity): string {
    let captain = team!.players!.find(p => p.role === 'Captain')
    return !captain ? '' : captain.nickname!
  }

  getCaptainAvatar(team: TournamentTeamEntity): string {
    let captain = team!.players!.find(p => p.role === 'Captain')
    return !captain ? '' : captain.avatar!
  }

  async scrollBehavior(event: Event) {
    if (!this.appendDataAllowed) {
      return
    }
    if (getElementScrollRemaining(event.target as HTMLElement) <= 20) {
      this.page = this.page + 1
      this.appendDataAllowed = false
      await this.fetchTournamentTeams()
    }
  }

  isTeamRegistered(team: TournamentTeamEntity): boolean {
    return team.state === TournamentTeamState.Registered
  }

  async subscribeRealtimeEvents() {
    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(
        this.$t('TournamentDetailsTeams.FailConnectServerMessage')
      )
      console.error(err)
    }
    this.realtimeConnection.on(
      'tournamentTeamsUpdated',
      this.onTournamentTeamsUpdated
    )
  }

  unsubscribeRealtimeEvents() {
    this.realtimeConnectionCancellationToken.cancel()
    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'tournamentTeamsUpdated',
        this.onTournamentTeamsUpdated
      )
    }
  }

  async onTournamentTeamsUpdated() {
    this.tournamentModule.resetSelectedTournamentTeams()
    this.$toast.info(
      this.$t('TournamentDetailsTeams.TournamentTeamListUpdated')
    )
    this.page = 1
    this.appendDataAllowed = true
    await this.fetchTournamentTeams()
  }

  onTournamentTeamSelected(teamId: string) {
    this.isTournamentTeamDialogVisible = true
    this.selectedTournamentTeamId = teamId
  }

  closeTournamentTeamDialog() {
    this.isTournamentTeamDialogVisible = false
    this.selectedTournamentTeamId = ''
  }
}
